/**
 * greenius® homebase — Anwendungsrahmen.
 *
 * Block 1 zeigt genau ein Modul: Produkte. Die spaetere Portalstruktur ist in
 * CORPORATE_DESIGN.md, Abschnitt 9 als Zielbild beschrieben und wird hier
 * ausdruecklich NICHT vorweggenommen -- es entstehen keine leeren Menuepunkte.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { MetaResponse, SessionUser } from "@homebase/shared";
import { api } from "./lib/api.ts";
import { Wordmark } from "./components/Brand.tsx";
import { Login } from "./components/Login.tsx";
import { ArticleList } from "./components/ArticleList.tsx";
import { ArticleDetailPanel, type LeaveGuard } from "./components/ArticleDetail.tsx";
import { ArticleCreate } from "./components/ArticleCreate.tsx";
import type { AusfuehrungsKontext } from "./components/ModelAssign.tsx";
import { ProductDossier } from "./components/ProductDossier.tsx";
import { ProductTrash } from "./components/ProductTrash.tsx";
import { Users } from "./components/Users.tsx";
import { SupplierPortal } from "./components/SupplierPortal.tsx";
import { ProcurementPortal } from "./components/ProcurementPortal.tsx";
import { Families } from "./components/Families.tsx";
import { ProductModels } from "./components/ProductModels.tsx";
import { PropertyReview } from "./components/PropertyReview.tsx";
import { Cleanup } from "./components/Cleanup.tsx";
import { CalculationParameters } from "./components/CalculationParameters.tsx";
import { PasswordChange } from "./components/PasswordChange.tsx";
import { LanguageSwitch } from "./components/LanguageSwitch.tsx";
import { gemerkteListenbreite, Splitter } from "./components/Splitter.tsx";
import { useI18n } from "./i18n/index.tsx";
import { errorText } from "./i18n/errors.ts";
import { ErrorState, LoadingState } from "./components/States.tsx";
import "./App.css";

type Phase = "checking" | "anonymous" | "loading" | "ready" | "failed";
type View = "articles" | "suppliers" | "procurement" | "models" | "families" | "review"
          | "cleanup" | "parameters" | "users";

export function App(): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [phase, setPhase] = useState<Phase>("checking");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [meta, setMeta] = useState<MetaResponse | null>(null);
  // Fehler als Ursache halten, nicht als fertigen Text -- so folgt die
  // Meldung einer Sprachumschaltung.
  const [error, setError] = useState<unknown>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  /* Die Breite der Produktliste neben einer offenen Akte -- vom Menschen
     gezogen und lokal gemerkt. `null` ist die Vorgabe aus dem Stylesheet. */
  const splitRef = useRef<HTMLDivElement>(null);
  const [listeBreite, setListeBreite] = useState<number | null>(() => gemerkteListenbreite());
  /*
   * Die zentrale Produktakte.
   *
   * Sie ist KEIN weiterer Bereich der Arbeitsansicht, sondern eine eigene
   * Flaeche: sie nimmt die ganze Breite und zeigt einen Artikel von der
   * Identitaet bis zum Verkaufspreis. Deshalb ein eigener Zustand und nicht
   * ein weiterer Wert in `view` -- sie gehoert zu einem ARTIKEL, nicht zu
   * einem Modul.
   */
  const [akteId, setAkteId] = useState<number | null>(null);
  /*
   * DIE RUECKFRAGE DES OFFENEN DETAILS.
   *
   * Ein Artikelwechsel wird in der LISTE ausgeloest, die vom Detail nichts
   * weiss. Stehen dort ungespeicherte Eingaben, waere der Klick auf die
   * naechste Zeile ein lautloser Datenverlust -- er sieht aus wie Blaettern.
   *
   * Deshalb laeuft der Wechsel durch diese Kennung. Ist nichts offen -- und
   * das ist der Normalfall --, ist sie `null` und der Klick wirkt unveraendert
   * sofort.
   */
  const detailGuard = useRef<LeaveGuard | null>(null);
  const openArticleGuarded = useCallback((next: number) => {
    const g = detailGuard.current;
    if (g) g(() => setOpenId(next)); else setOpenId(next);
  }, []);
  const [creating, setCreating] = useState(false);
  /* Produktmodelle 002A: der Produktassistent aus einer Ausfuehrung heraus --
     derselbe Assistent, mit Kontext. Danach zurueck zum Modell. */
  const [anlageKontext, setAnlageKontext] = useState<AusfuehrungsKontext | null>(null);
  const [modellNeuLaden, setModellNeuLaden] = useState(0);
  const [openModelId, setOpenModelId] = useState<number | null>(null);
  /* Aus der Produktakte ins Lieferantenportal -- zu genau einem Lieferanten. */
  const [openSupplierId, setOpenSupplierId] = useState<number | null>(null);
  const [supplierFokus, setSupplierFokus] = useState<"bewertung" | null>(null);
  const [view, setView] = useState<View>("articles");
  /* Der Papierkorb ist keine eigene Ansicht der Hauptnavigation, sondern ein
     Zustand innerhalb der Produkte -- deshalb ein Schalter, kein `View`. */
  const [trashOpen, setTrashOpen] = useState(false);
  /* Nach Wiederherstellen oder endgueltigem Loeschen hat sich der Bestand
     geaendert; die Liste laedt dann neu. */
  const [metaReload, setMetaReload] = useState(0);
  /* Zaehler statt Zustand: die Familienseite haelt ihr offenes Detail
     selbst; ein Klick auf den aktiven Menuepunkt setzt sie zurueck. */
  const [familieOffen, setFamilieOffen] = useState(0);
  const [changingPassword, setChangingPassword] = useState(false);
  // Der Hinweis nach einer Passwortaenderung wird als Merker gehalten, nicht
  // als Text -- sonst bliebe er in der Sprache von vorhin stehen.
  const [passwordChangedNote, setPasswordChangedNote] = useState(false);

  // Die Oberflaeche richtet sich nach den tatsaechlich vorhandenen
  // Faehigkeiten -- nicht nach dem Rollennamen. Kommt spaeter eine dritte
  // Rolle hinzu, muss hier nichts geaendert werden.
  const mayManageUsers = user?.capabilities.includes("user.manage") ?? false;
  /*
   * Die zentralen Kalkulationsparameter tragen Einkaufskonditionen -- Kurse,
   * Frachtsaetze, Abgaben. Sie stehen hinter demselben Recht wie jede andere
   * Einkaufszahl (`purchasing.read`); geaendert werden sie mit
   * `price.parameter.write`. Ein Viewer sieht den Menuepunkt nicht, und der
   * Server weist den Zugriff ohnehin ab.
   */
  const mayReadParams = user?.capabilities.includes("purchasing.read") ?? false;
  const mayWriteParams = user?.capabilities.includes("price.parameter.write") ?? false;
  const mayWriteTexts = user?.capabilities.includes("text.write") ?? false;
  const mayReleaseTexts = user?.capabilities.includes("text.release") ?? false;
  const mayWriteProps = user?.capabilities.includes("property.write") ?? false;
  const mayConfirmProps = user?.capabilities.includes("property.confirm") ?? false;
  /*
   * DIE INTERNE PRUEFSICHT.
   *
   * Sie entscheidet, ob es den Arbeitsbereich "Merkmalsprüfung" fuer dieses
   * Konto ueberhaupt gibt. Das Ausblenden ist Bequemlichkeit und keine
   * Sicherung -- der Server liefert die Daten ohne dieses Recht nicht mehr,
   * und die Adresse antwortet mit 403. Genau darin lag der Befund: der
   * Menuepunkt war da, und die Adresse lieferte.
   */
  const mayReadReview = user?.capabilities.includes("property.review.read") ?? false;
  /*
   * Das Lieferantenportal zeigt Konditionen, Preise und Bewertungen --
   * dieselbe Vertraulichkeit wie der Arbeitsbereich Einkauf. Ohne
   * `purchasing.read` gibt es diesen Bereich fuer dieses Konto nicht:
   * kein Menuepunkt, keine leere Seite. Den eigentlichen Schutz haelt
   * der Server.
   */
  const mayReadSuppliers = user?.capabilities.includes("purchasing.read") ?? false;
  /*
   * DIE PRODUKTWELT -- zwei Sichten, ein Hauptpunkt (§6).
   *
   * `articles` und `models` sind Sichten auf denselben Bestand: eine
   * nach SKU, eine nach Modell. Der Hauptpunkt „Produkte" ist bei
   * beiden markiert, und die Umschaltung steht im Arbeitsbereich.
   */
  const weltAktiv = view === "articles" || view === "models";
  /* Das Produktions- und Logistikportal ist ein eigener Arbeitsbereich mit
     eigenem Recht -- wer Preise sehen darf, muss nicht Bestellungen
     fuehren duerfen. */
  const mayReadProcurement = user?.capabilities.includes("procurement.read") ?? false;
  const mayWriteModels = user?.capabilities.includes("model.write") ?? false;
  // Eigenes Recht: die DTE-Listung wirkt in ein Fremdsystem und ist deshalb
  // von "Artikel bearbeiten" getrennt (PRODUCT_DATA_MODEL.md 22.4).
  const mayListDte = user?.capabilities.includes("dte.listing") ?? false;
  // Zwei getrennte Rechte, wie bei Texten und Merkmalen: wer erfasst,
  // bestaetigt damit noch nicht. Die Oberflaeche richtet sich danach;
  // entschieden wird im Server.
  const mayWriteLogistics = user?.capabilities.includes("logistics.write") ?? false;
  const mayConfirmLogistics = user?.capabilities.includes("logistics.confirm") ?? false;
  // Grunddaten anlegen und aendern. Die Akte selbst darf jeder oeffnen, der
  // Artikel lesen darf -- was er darin aendern kann, entscheidet der Server.
  const mayWriteArticle = user?.capabilities.includes("article.write") ?? false;

  /** Zurueck zum zentralen Einstieg: Artikelliste, ohne offene Nebenansicht. */
  const goHome = useCallback(() => {
    setView("articles"); setAkteId(null); setOpenId(null); setTrashOpen(false);
    setCreating(false); setOpenModelId(null);
  }, []);

  /*
   * Der Uebergang muss in BEIDE Richtungen fuehren (PRODUCT_DATA_MODEL.md 23.3).
   *
   * Von einer SKU zum Produktmodell und vom Modell zu seinen SKUs -- sonst
   * geht der Zusammenhang genau dort verloren, wo er gebraucht wird. Beide
   * Spruenge wechseln den Bereich und oeffnen das Ziel unmittelbar.
   */
  const openModel = useCallback((id: number) => { setOpenModelId(id); setView("models"); }, []);
  /* Mit Fokus „bewertung" oeffnet die Lieferantenakte dort, wo die Note
     herkommt -- der Klick auf „AGC ★ 4,0" im Einkauf (UX-Nachtrag 002). */
  const openSupplier = useCallback((id: number, fokus?: "bewertung") => {
    setSupplierFokus(fokus ?? null); setOpenSupplierId(id); setView("suppliers");
  }, []);
  const openArticle = useCallback((id: number) => { setOpenId(id); setView("articles"); }, []);
  /* Aus der Sendung in die Geschaeftspartner -- fuer den Fall, dass noch
     kein Logistikpartner angelegt ist (UX-Polish 002, Abschnitt 16). Es
     entsteht keine zweite Adressverwaltung; der Sprung fuehrt dorthin,
     wo die Stammdaten ohnehin liegen. */
  const openPartners = useCallback(() => {
    setOpenSupplierId(null); setSupplierFokus(null); setView("suppliers");
  }, []);

  /*
   * STILL NACHLADEN -- ohne die Seite auszuraeumen (Lifecycle 001).
   *
   * `loadMeta` setzt die Phase auf „loading"; damit verschwindet die ganze
   * Arbeitsflaeche kurz, und ein offenes Detail schliesst sich. Fuer eine
   * Zustandsaenderung an einer Struktur ist das zu viel: der Mensch steht
   * gerade in dieser Struktur und arbeitet weiter.
   */
  const refreshMeta = useCallback(async () => {
    try { setMeta(await api.meta()); } catch { /* der naechste Aufruf zeigt den Fehler */ }
  }, []);

  const loadMeta = useCallback(async () => {
    setPhase("loading");
    try {
      setMeta(await api.meta());
      setPhase("ready");
    } catch (e) {
      setError(e);
      setPhase("failed");
    }
  }, []);

  // Browser-Titel folgt dem Kontext und der Sprache. Der Markenname wird
  // nicht uebersetzt. Die Anmeldeseite setzt ihren Titel selbst.
  useEffect(() => {
    if (phase === "anonymous") return;
    if (phase !== "ready") { document.title = "greenius® homebase"; return; }
    const page = view === "users" ? t("nav.users")
               : view === "suppliers" ? t("nav.suppliers")
               : view === "procurement" ? t("nav.procurement")
               : view === "families" ? t("nav.families")
               : view === "models" ? t("nav.models")
               : view === "review" ? t("nav.review")
               : view === "cleanup" ? t("nav.cleanup")
               : view === "parameters" ? t("nav.parameters")
               : t("nav.products");
    document.title = `${page} | greenius® homebase`;
  }, [phase, view, t]);

  useEffect(() => {
    api.session()
      .then((r) => {
        if (r.user) { setUser(r.user); void loadMeta(); }
        else setPhase("anonymous");
      })
      .catch((e) => { setError(e); setPhase("failed"); });
  }, [loadMeta]);

  async function signOut(): Promise<void> {
    await api.logout().catch(() => undefined);
    setUser(null); setMeta(null); setOpenId(null); setOpenModelId(null); setOpenSupplierId(null); setView("articles");
    setAkteId(null); setCreating(false);
    setPasswordChangedNote(false); setPhase("anonymous");
  }

  if (phase === "checking") {
    return <div className="app app--center"><LoadingState label={t("app.checkingSession")} /></div>;
  }
  if (phase === "anonymous") {
    return (
      <Login
        notice={passwordChangedNote ? t("login.passwordChanged") : null}
        onSignedIn={(u) => { setPasswordChangedNote(false); setUser(u); void loadMeta(); }}
      />
    );
  }

  return (
    <div className="app">
      <header className="app__header">
        {/*
          * DAS LOGO FUEHRT NACH HAUSE.
          *
          * Bis hierher war es ein Bild ohne Wirkung -- der einzige Ort der
          * Anwendung, an dem ein Klick nichts tat. Genau dort klickt man
          * aber, wenn man sich verlaufen hat.
          *
          * Es fuehrt zur Artikelliste und schliesst dabei Akte und
          * Detailansicht: ein Einstieg, der die halbe alte Ansicht stehen
          * laesst, ist kein Einstieg. INTERNES Umschalten, kein Neuladen --
          * die Anwendung bleibt, wo sie ist.
          *
          * Ein Dashboard gibt es noch nicht; sobald es eines gibt, ist dies
          * die eine Stelle, die sich aendert.
          */}
        <button className="app__home" onClick={goHome} aria-label={t("nav.home")} title={t("nav.home")}>
          <Wordmark />
        </button>
        {/*
          * EIN KLICK AUF DEN AKTIVEN BEREICH FUEHRT ZURUECK (003 V2).
          *
          * Wer in einem offenen Detail steht und den Menuepunkt erneut
          * anklickt, meint „zurueck zur Liste" -- bis hierher geschah
          * nichts, und der Weg fuehrte ueber einen fremden Bereich und
          * zurueck. `zumBereich` schliesst deshalb das Detail DIESES
          * Bereichs, wenn er schon offen ist, und wechselt sonst nur.
          */}
        <nav className="app__nav" aria-label={t("nav.sections")}>
          {/*
            * ── DIE PRODUKTWELT IST EIN HAUPTPUNKT (§6) ─────────────
            *
            * Produkt und Produktmodell sind zwei Sichten auf denselben
            * Bestand -- nicht zwei Bereiche. Der Hauptpunkt bleibt
            * deshalb markiert, egal welche der beiden Sichten offen
            * ist; GEWECHSELT wird in der Umschaltung oberhalb des
            * Arbeitsbereichs, nicht hier oben.
            */}
          <button type="button"
            className={`app__nav-item ${weltAktiv ? "app__nav-item--active" : ""}`}
            aria-current={weltAktiv ? "page" : undefined}
            onClick={() => { if (view === "articles") { setOpenId(null); setAkteId(null); } setView("articles"); }}>{t("nav.products")}</button>
          {/*
            * DAS LIEFERANTENPORTAL steht direkt neben den Produkten: es
            * ist die zweite zentrale Sicht auf denselben Bestand -- vom
            * Partner aus statt vom Produkt aus.
            */}
          {mayReadSuppliers && (
            <button type="button"
              className={`app__nav-item ${view === "suppliers" ? "app__nav-item--active" : ""}`}
              aria-current={view === "suppliers" ? "page" : undefined}
              onClick={() => { setOpenSupplierId(null); setSupplierFokus(null); setView("suppliers"); }}>{t("nav.suppliers")}</button>
          )}
          {/*
            * PRODUKTION & LOGISTIK -- der operative Arbeitsbereich. Ohne
            * `procurement.read` gibt es ihn fuer dieses Konto nicht: kein
            * Menuepunkt, keine leere Seite, kein Hinweis auf etwas, das man
            * nicht sehen darf.
            */}
          {mayReadProcurement && (
            <button type="button"
              className={`app__nav-item ${view === "procurement" ? "app__nav-item--active" : ""}`}
              aria-current={view === "procurement" ? "page" : undefined}
              onClick={() => setView("procurement")}>{t("nav.procurement")}</button>
          )}
          {/*
            * PRODUKTMODELLE STEHEN HIER NICHT MEHR (§6). Sie sind
            * keine eigene Welt, sondern die zweite Sicht auf die
            * Produktwelt -- erreichbar ueber die Umschaltung im
            * Arbeitsbereich. Die Ansicht selbst ist unveraendert; nur
            * ihr Zugang hat sich geaendert.
            */}
          {/* Produktfamilien darf jeder lesen, der Artikel lesen darf --
              es entsteht keine eigene Berechtigung. */}
          <button type="button"
            className={`app__nav-item ${view === "families" ? "app__nav-item--active" : ""}`}
            aria-current={view === "families" ? "page" : undefined}
            onClick={() => { if (view === "families") setFamilieOffen((n) => n + 1); setView("families"); }}>{t("nav.families")}</button>
          {/* Die Pruefwarteschlange ist ein interner Arbeitsbereich. Ohne
              `property.review.read` gibt es ihn fuer dieses Konto nicht --
              kein Menuepunkt, keine leere Seite, kein Hinweis auf etwas, das
              man nicht sehen darf. Bestaetigen verlangt zusaetzlich
              `property.confirm`; beides prueft der Server. */}
          {mayReadReview && (
            <button type="button"
              className={`app__nav-item ${view === "review" ? "app__nav-item--active" : ""}`}
              aria-current={view === "review" ? "page" : undefined}
              onClick={() => setView("review")}>{t("nav.review")}</button>
          )}
          {/* Die Bereinigung zeigt Befunde, Quellwortlaute und
              Qualitaetsstati -- dasselbe, was `property.review.read`
              ueberall sonst schuetzt. Deshalb dasselbe Recht und nicht
              `article.read`: sonst waere sie der Weg um die Grenze herum.
              Der Server prueft es ohnehin. */}
          {mayReadReview && (
            <button type="button"
              className={`app__nav-item ${view === "cleanup" ? "app__nav-item--active" : ""}`}
              aria-current={view === "cleanup" ? "page" : undefined}
              onClick={() => setView("cleanup")}>{t("nav.cleanup")}</button>
          )}
          {/* Die zentralen Kalkulationsparameter gelten fuer das ganze Haus und
              haengen an keinem Artikel. Sie stehen deshalb in der Leiste und
              nicht in der Produktakte -- und nur fuer Konten, die
              Einkaufszahlen sehen duerfen. */}
          {mayReadParams && (
            <button type="button"
              className={`app__nav-item ${view === "parameters" ? "app__nav-item--active" : ""}`}
              aria-current={view === "parameters" ? "page" : undefined}
              onClick={() => setView("parameters")}>{t("nav.parameters")}</button>
          )}
          {/* Nur sichtbar mit user.manage. Das Ausblenden ist Bequemlichkeit --
              der Server weist den Zugriff ohnehin mit 403 ab. */}
          {mayManageUsers && (
            <button type="button"
              className={`app__nav-item ${view === "users" ? "app__nav-item--active" : ""}`}
              aria-current={view === "users" ? "page" : undefined}
              onClick={() => setView("users")}>{t("nav.users")}</button>
          )}
          {/*
            * NORA HR — UND WARUM ES HIER EIN LINK IST UND KEINE SCHALTFLAECHE.
            *
            * Jeder andere Punkt dieser Leiste schaltet eine ANSICHT um:
            * dieselbe Anwendung, anderer Inhalt. Nora HR tut etwas anderes --
            * es verlaesst HOMEBASE. Das ist ein `<a href>`, kein `onClick`:
            * der Browser weiss dann von selbst, was zu tun ist, und
            * Tastatur, Mittelklick und Statuszeile funktionieren, ohne dass
            * jemand sie nachbaut.
            *
            * DIE ADRESSE KOMMT AUS DEM SERVER. Hier wird nichts
            * zusammengesetzt: das erlaubte Ziel steht in `HANDOFF_TARGETS`
            * und nirgends sonst. Die Oberflaeche kennt es nicht einmal.
            *
            * IST DIE UEBERGABE NICHT EINGERICHTET, gibt es den Punkt nicht.
            * Ein Menuepunkt, der in eine 503 fuehrt, ist schlimmer als
            * keiner.
            *
            * Und es steht am rechten Rand der Leiste, abgesetzt von den
            * HOMEBASE-eigenen Bereichen. Das ist keine Kosmetik: die
            * anderen fuehren nach innen, dieser nach draussen. Ein Punkt,
            * der wie die anderen aussaehe, waere eine falsche Aussage.
            */}
          {meta?.noraHandoff && (
            <a className="app__nav-item app__nav-item--extern" href={meta.noraHandoff}>
              {t("nav.nora")}
            </a>
          )}
        </nav>
        <div className="app__user">
          <span className="app__user-name">{user?.displayName}</span>
          {/* Die Rolle wird aus dem Code beschriftet, nicht aus dem Namen der
              Datenbank -- so folgt sie der Sprache. */}
          {user?.role && (
            <span className="app__role" title={t("nav.roleTitle", { role: i18n.tCode("userRole", user.role) })}>
              {i18n.tCode("userRole", user.role)}
            </span>
          )}
          {/* Steht jedem Konto offen -- Administrator wie Viewer. Eine
              persoenliche Sicherheitsfunktion, keine fachliche Berechtigung. */}
          <button className="btn" onClick={() => setChangingPassword(true)}>{t("nav.changePassword")}</button>
          <button className="btn" onClick={signOut}>{t("nav.signOut")}</button>
          <LanguageSwitch />
        </div>
      </header>

      {/*
        * `--fensterhoch` NUR fuer Master/Detail (§5). Dort arbeiten
        * zwei Bereiche nebeneinander, die man abwechselnd benutzt --
        * jeder mit eigenem Bildlauf. Jede andere Seite waechst und
        * laesst das Fenster scrollen.
        */}
      <main className={`app__main${
        phase === "ready" && meta && weltAktiv && akteId === null && !trashOpen
          ? " app__main--fensterhoch" : ""}`}>
        {/*
          * ── DIE ANSICHTSUMSCHALTUNG DER PRODUKTWELT (§6) ───────────
          *
          * SICHTBAR, NICHT VERSTECKT. Kein Dropdown, kein Untermenue,
          * keine zweite Navigationsebene am Rand: zwei Schaltflaechen
          * direkt ueber dem Arbeitsbereich, und die aktive ist zu
          * sehen.
          *
          * SIE IST EINE ANSICHTSUMSCHALTUNG UND KEIN FILTER (§14).
          * Ein Filter verkleinert eine Menge; hier wechselt die ganze
          * Sicht. Deshalb `role="tablist"` und nicht eine Reihe von
          * Knoepfen -- Vorleseprogramme sagen damit „Reiter 1 von 2"
          * statt „Schaltflaeche".
          *
          * NICHT IN DER PRODUKTAKTE. Sie verdraengt Liste und Detail
          * ganz; eine Umschaltung darueber boete einen Wechsel an,
          * der die offene Akte verwirft.
          */}
        {phase === "ready" && meta && weltAktiv && akteId === null && (
          <div className="welt__segmente" role="tablist" aria-label={t("nav.products")}>
            <button type="button" role="tab" className="welt__segment"
              aria-selected={view === "articles"}
              onClick={() => { if (view === "articles") { setOpenId(null); setAkteId(null); } setView("articles"); }}>
              {t("nav.products")}
            </button>
            <button type="button" role="tab" className="welt__segment"
              aria-selected={view === "models"}
              onClick={() => { if (view === "models") setOpenModelId(null); setView("models"); }}>
              {t("nav.models")}
            </button>
          </div>
        )}
        {phase === "loading" && <LoadingState label={t("app.loadingMeta")} />}
        {phase === "failed" && <ErrorState message={errorText(i18n, error)} onRetry={loadMeta} />}
        {/*
          * Die Akte verdraengt Liste und Detail, statt sich daneben zu
          * stellen. Zehn Abschnitte in einer halben Fensterbreite waeren
          * keine Akte, sondern eine Spalte.
          */}
        {phase === "ready" && meta && view === "articles" && akteId !== null && (
          <ProductDossier articleId={akteId} meta={meta}
            capabilities={user?.capabilities ?? []}
            mayWriteArticle={mayWriteArticle}
            mayWriteProps={mayWriteProps} mayConfirmProps={mayConfirmProps}
            mayWriteLogistics={mayWriteLogistics} mayConfirmLogistics={mayConfirmLogistics}
            onBack={() => setAkteId(null)}
            onTrashed={mayWriteArticle
              ? () => { setAkteId(null); setOpenId(null); setMetaReload((n) => n + 1); }
              : undefined} />
        )}
        {/*
          * DER PAPIERKORB -- eine Ansicht INNERHALB der Produkte.
          *
          * Kein eigener Eintrag in der Hauptleiste: er waere dauerhaft
          * sichtbar und meistens leer. Er loest die Produktliste ab, solange
          * er offen ist, und gibt sie danach unveraendert zurueck.
          */}
        {phase === "ready" && meta && view === "articles" && akteId === null && trashOpen && (
          <ProductTrash
            mayWrite={mayWriteArticle}
            mayDelete={(user?.capabilities ?? []).includes("article.delete")}
            onChanged={() => setMetaReload((n) => n + 1)}
            onBack={() => setTrashOpen(false)} />
        )}
        {phase === "ready" && meta && view === "articles" && akteId === null && !trashOpen && (
          <div className={openId !== null ? "app__split app__split--detail" : "app__split"}
            ref={splitRef}
            style={listeBreite !== null
              ? { ["--liste-breite" as string]: `${listeBreite}px` } as React.CSSProperties : undefined}>
            <ArticleList meta={meta} onOpen={openArticleGuarded} compact={openId !== null} selectedId={openId}
              key={metaReload}
              onCreate={mayWriteArticle ? () => setCreating(true) : undefined}
              onOpenTrash={() => setTrashOpen(true)} />
            {openId !== null && (
              <Splitter container={splitRef} breite={listeBreite} onBreite={setListeBreite} />
            )}
            {openId !== null && (
              <ArticleDetailPanel id={openId} onClose={() => setOpenId(null)}
                capabilities={user?.capabilities ?? []}
                mayWrite={mayWriteTexts} mayRelease={mayReleaseTexts} meta={meta}
                mayWriteProps={mayWriteProps} mayConfirmProps={mayConfirmProps}
                mayWriteModels={mayWriteModels} mayListDte={mayListDte}
                mayWriteLogistics={mayWriteLogistics} mayConfirmLogistics={mayConfirmLogistics}
                onOpenModel={openModel} onOpenArticle={openArticle}
                onOpenSupplier={openSupplier}
                onTrashed={mayWriteArticle
                  ? () => { setOpenId(null); setMetaReload((n) => n + 1); }
                  : undefined}
                onOpenDossier={mayWriteArticle ? setAkteId : undefined}
                guardRef={detailGuard} />
            )}
          </div>
        )}
        {phase === "ready" && meta && view === "suppliers" && mayReadSuppliers && user && (
          <SupplierPortal meta={meta} capabilities={user.capabilities}
            openId={openSupplierId} onOpen={(sid) => { setSupplierFokus(null); setOpenSupplierId(sid); }}
            fokus={supplierFokus}
            /*
             * Ein geaenderter Partnerbestand geht andere Bereiche an --
             * die Sendungsakte liest `meta.partners`, um die
             * Logistikpartner anzubieten. `refreshMeta` holt es ohne
             * Flackern nach; `loadMeta` wuerde die Arbeitsflaeche kurz
             * leeren und ein offenes Detail schliessen.
             */
            onPartnersChanged={() => void refreshMeta()}
            onOpenArticle={openArticle} />
        )}

        {phase === "ready" && meta && view === "procurement" && mayReadProcurement && user && (
          <ProcurementPortal meta={meta} capabilities={user.capabilities}
            onOpenPartners={openPartners} />
        )}

        {phase === "ready" && meta && view === "models" && (
          <ProductModels meta={meta}
            mayWriteModels={mayWriteModels}
            mayWriteLogistics={mayWriteLogistics}
            mayWriteTexts={mayWriteTexts} mayReleaseTexts={mayReleaseTexts}
            mayWriteProps={mayWriteProps} mayConfirmProps={mayConfirmProps}
            mayWriteDocuments={user?.capabilities.includes("document.write") ?? false}
            mayCreateArticle={mayWriteArticle}
            mayCreateCategory={user?.capabilities.includes("system.manage") ?? false}
            mayDeleteModels={mayWriteModels && (user?.capabilities.includes("article.delete") ?? false)}
            reloadToken={modellNeuLaden}
            onNeuerArtikel={(k) => { setAnlageKontext(k); setCreating(true); }}
            openId={openModelId} onOpen={setOpenModelId} onOpenArticle={openArticle} />
        )}
        {/* Auch die Ansicht selbst haengt am Recht: eine `view`, die ueber
            einen wiederhergestellten Zustand erreichbar bliebe, waere ein
            zweiter Weg zu derselben Seite. */}
        {phase === "ready" && meta && view === "review" && mayReadReview && (
          <PropertyReview meta={meta}
            mayWrite={mayWriteProps} mayConfirm={mayConfirmProps}
            onOpenArticle={openArticle} />
        )}
        {phase === "ready" && meta && view === "cleanup" && mayReadReview && (
          <Cleanup mayWrite={mayWriteArticle} onOpenArticle={openArticle} />
        )}
        {phase === "ready" && meta && view === "families" && (
          <Families key={`fam-${familieOffen}`} meta={meta} mayWrite={mayWriteTexts} mayRelease={mayReleaseTexts}
            mayWriteProps={mayWriteProps} mayConfirmProps={mayConfirmProps}
            mayManageFamilies={mayWriteArticle}
            mayManageCategories={user?.capabilities.includes("system.manage") ?? false}
            mayDeleteStructures={user?.capabilities.includes("article.delete") ?? false}
            onStructureChanged={() => void refreshMeta()} />
        )}
        {phase === "ready" && view === "parameters" && mayReadParams && (
          <CalculationParameters mayWrite={mayWriteParams} />
        )}
        {phase === "ready" && view === "users" && user && <Users currentUserId={user.id} />}
      </main>

      {/*
        * Ein neu angelegter Artikel wird unmittelbar in seiner Akte
        * geoeffnet. Anlegen ist ein Anfang, kein Abschluss -- die Angaben
        * fehlen ja noch alle.
        */}
      {creating && meta && (
        <ArticleCreate meta={meta} ausfuehrung={anlageKontext}
          onClose={() => { setCreating(false); setAnlageKontext(null); }}
          onCreated={(id) => {
            setCreating(false);
            /* Aus einer Ausfuehrung heraus angelegt: zurueck zum Modell, dessen
               Matrix jetzt die neue Artikelnummer zeigt. */
            if (anlageKontext !== null) {
              setAnlageKontext(null); setModellNeuLaden((n) => n + 1);
              setOpenModelId(anlageKontext.modelId); setView("models"); return;
            }
            setOpenId(id); setAkteId(id);
          }}
          /* Wer wegen des Papierkorbs nicht anlegen kann, muss dorthin
             kommen -- sonst ist die Sperre eine Sackgasse. */
          onOpenTrash={() => { setCreating(false); setAkteId(null); setTrashOpen(true); }}
          /* Der Workflow ist Wegweisung, keine Rechteerweiterung: das
             Modellrecht ist dasselbe wie im Modellbereich. */
          mayWriteModels={mayWriteModels} />
      )}

      {changingPassword && (
        <PasswordChange
          onClose={() => setChangingPassword(false)}
          onChanged={() => {
            // Der Server hat die Sitzung bereits verworfen und das Cookie
            // geloescht. Die Oberflaeche zieht nur nach.
            setChangingPassword(false);
            setUser(null); setMeta(null); setOpenId(null); setOpenModelId(null); setOpenSupplierId(null); setView("articles");
            setAkteId(null); setCreating(false);
            setPasswordChangedNote(true);
            setPhase("anonymous");
          }} />
      )}
    </div>
  );
}
